import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import { Badge } from '@/common/ui/badge'
import { Skeleton } from '@/common/ui/skeleton'
import { Progress } from '@/common/ui/progress'
import { TaskMetric } from '../../type'
import { Server, Cpu, HardDrive, ArrowRight } from 'lucide-react'
import TaskDetailModal from '../TaskDetailModal'

interface TaskMetricsTableProps {
	tasks: TaskMetric[]
	isLoading?: boolean
}

export default function TaskMetricsTable({
	tasks,
	isLoading = false,
}: TaskMetricsTableProps) {
	const [selectedTask, setSelectedTask] = useState<TaskMetric | null>(null)
	const [isModalOpen, setIsModalOpen] = useState(false)

	const formatPercentage = (value: number | null): string => {
		if (value === null) return 'N/A'
		return `${value.toFixed(2)}%`
	}

	const handleTaskClick = (task: TaskMetric) => {
		setSelectedTask(task)
		setIsModalOpen(true)
	}

	const getStatusColor = (value: number | null): string => {
		if (value === null) return 'text-muted-foreground'
		if (value < 50) return 'text-green-600 dark:text-green-400'
		if (value < 80) return 'text-yellow-600 dark:text-yellow-400'
		return 'text-red-600 dark:text-red-400'
	}

	if (isLoading) {
		return (
			<Card className="border-2">
				<CardHeader className="bg-gradient-to-r from-muted/50 to-background">
					<CardTitle className="flex items-center gap-3">
						<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 shadow-md">
							<Server className="h-5 w-5 text-white" />
						</div>
						<span className="text-xl font-bold">Tasks Metrics</span>
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						{[...Array(3)].map((_, i) => (
							<div key={i} className="space-y-2">
								<Skeleton className="h-4 w-full" />
								<Skeleton className="h-4 w-3/4" />
							</div>
						))}
					</div>
				</CardContent>
			</Card>
		)
	}

	if (!tasks || tasks.length === 0) {
		return (
			<Card className="border-2">
				<CardHeader className="bg-gradient-to-r from-muted/50 to-background">
					<CardTitle className="flex items-center gap-3">
						<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 shadow-md">
							<Server className="h-5 w-5 text-white" />
						</div>
						<span className="text-xl font-bold">Tasks Metrics</span>
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex h-40 items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/20">
						<p className="text-muted-foreground">ไม่มีข้อมูล Tasks</p>
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card className="overflow-hidden border-2">
			<CardHeader className="border-b bg-gradient-to-r from-muted/50 to-background">
				<CardTitle className="flex items-center gap-3">
					<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 shadow-md">
						<Server className="h-5 w-5 text-white" />
					</div>
					<span className="text-xl font-bold">
						Tasks Metrics{' '}
						<span className="text-base font-normal text-muted-foreground">
							({tasks.length} tasks)
						</span>
					</span>
				</CardTitle>
			</CardHeader>
			<CardContent className="p-0">
				<div className="overflow-x-auto">
					<Table>
						<TableHeader>
							<TableRow className="bg-muted/30">
								<TableHead className="font-semibold">Task Name</TableHead>
								<TableHead>
									<div className="flex items-center gap-2">
										<Cpu className="h-4 w-4 text-blue-600" />
										<span className="font-semibold">CPU</span>
									</div>
								</TableHead>
								<TableHead>
									<div className="flex items-center gap-2">
										<HardDrive className="h-4 w-4 text-purple-600" />
										<span className="font-semibold">Memory</span>
									</div>
								</TableHead>
								<TableHead className="font-semibold">Status</TableHead>
								<TableHead className="w-12"></TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{tasks.map((task, index) => (
								<TableRow
									key={`${task.taskName}-${index}`}
									className="group cursor-pointer border-b transition-all hover:bg-gradient-to-r hover:from-muted/50 hover:to-transparent"
									onClick={() => handleTaskClick(task)}
								>
									<TableCell className="font-semibold">
										{task.taskName}
									</TableCell>
									<TableCell>
										<div className="min-w-[120px] space-y-2">
											<div className="flex items-center justify-between gap-2">
												<span
													className={`text-sm font-medium ${getStatusColor(task.cpu)}`}
												>
													{formatPercentage(task.cpu)}
												</span>
											</div>
											{task.cpu !== null && (
												<Progress value={task.cpu} className="h-1.5" />
											)}
										</div>
									</TableCell>
									<TableCell>
										<div className="min-w-[120px] space-y-2">
											<div className="flex items-center justify-between gap-2">
												<span
													className={`text-sm font-medium ${getStatusColor(task.memory)}`}
												>
													{formatPercentage(task.memory)}
												</span>
											</div>
											{task.memory !== null && (
												<Progress value={task.memory} className="h-1.5" />
											)}
										</div>
									</TableCell>
									<TableCell>
										<Badge
											variant={
												task.lastStatus === 'RUNNING'
													? 'default'
													: task.lastStatus === 'PENDING'
														? 'secondary'
														: 'outline'
											}
											className="font-medium"
										>
											{task.lastStatus}
										</Badge>
									</TableCell>
									<TableCell>
										<ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			</CardContent>

			{/* Task Detail Modal */}
			<TaskDetailModal
				task={selectedTask}
				open={isModalOpen}
				onOpenChange={setIsModalOpen}
			/>
		</Card>
	)
}
